import { Controller } from "@hotwired/stimulus"

// Landing page motion.
// - Hero 3D stage (landing/_hero_stage): everything is driven through CSS variables, so the transforms stay in
//   CSS. The pointer tilts the scene (--tilt-x/--tilt-y). Scrolling turns the front phone to face you, swings
//   the back phone away, slides the four words apart and moves the front screen through feed, map and chat.
//   Desktop pins the hero for that; phones scrub it while the stage passes through the viewport.
// - Reveals, the kinetic band, the header and the progress bar are CSS scroll-driven animations (landing.css,
//   behind @supports (animation-timeline: view())); without that support the reveals fall back to GSAP here.
// - Service scenes (landing/scenes/*) are CSS loops that run only while the scene is on screen (.is-live).
// - Where the service cards pin (wide and tall viewports), the covered card shrinks and shades as the next one
//   slides over it (GSAP).
// gsap is imported lazily so it stays out of the eager application bundle.
export default class extends Controller {
  static targets = [ "reveal", "stackCard", "stage", "phoneFront", "phoneBack", "screen", "word", "scene" ]

  connect() {
    this.cleanups = []
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.bindTilt()
    this.observeScenes()
    this.start()
  }

  disconnect() {
    this.disconnected = true
    this.cleanups.forEach(cleanup => cleanup())
  }

  async start() {
    const [{ gsap }, { ScrollTrigger }] = await Promise.all([ import("gsap"), import("gsap/ScrollTrigger") ])
    if (this.disconnected) return
    gsap.registerPlugin(ScrollTrigger)

    const media = gsap.matchMedia()
    media.add({ desktop: "(min-width: 1024px)", mobile: "(max-width: 1023px)" }, ({ conditions }) => {
      this.heroChoreography(gsap, conditions.desktop)
    })
    // Matches the sticky breakpoint in landing.css
    media.add("(min-width: 1024px) and (min-height: 700px)", () => this.stackRecede(gsap))

    const context = gsap.context(() => {
      if (!CSS.supports("animation-timeline: view()")) this.fallbackReveals(gsap)
    }, this.element)

    this.cleanups.push(() => { media.revert(); context.revert() })
  }

  heroChoreography(gsap, pinned) {
    if (!this.hasStageTarget || !this.hasPhoneFrontTarget) return

    const hero = this.stageTarget.closest("section")
    const header = this.element.querySelector(".landing-header")
    const timeline = gsap.timeline({
      defaults: { ease: "none" },
      scrollTrigger: pinned
        ? { trigger: hero, start: () => `top ${header?.offsetHeight ?? 0}px`, end: "+=90%", pin: true, scrub: 0.6, invalidateOnRefresh: true }
        : { trigger: this.stageTarget, start: "top 70%", end: "bottom 15%", scrub: 0.6 }
    })

    timeline.to(this.phoneFrontTarget, { "--rz": "0deg", "--ry": "0deg", "--x": "-14cqw", "--s": 1.12, duration: 1 }, 0)
    if (this.hasPhoneBackTarget) {
      // Explicit start opacity: the CSS entrance animation may still hold it at 0 when the timeline records it
      timeline.fromTo(this.phoneBackTarget, { autoAlpha: 1 }, { "--x": "-46cqw", "--rz": "-42deg", "--ry": "34deg", autoAlpha: 0, duration: 0.7 }, 0)
    }
    this.wordTargets.forEach((word, index) => {
      timeline.to(word, { "--wx": `${index % 2 ? 14 : -14}cqw`, duration: 1 }, 0)
    })

    // Screens cross-fade in order: feed → map → chat
    this.screenTargets.slice(1).forEach((screen, index) => {
      timeline.fromTo(screen, { autoAlpha: 0, y: "6%" }, { autoAlpha: 1, y: "0%", duration: 0.18 }, 0.3 + index * 0.33)
    })
  }

  observeScenes() {
    if (!this.hasSceneTarget) return

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => entry.target.classList.toggle("is-live", entry.isIntersecting))
    }, { threshold: 0.35 })
    this.sceneTargets.forEach(scene => observer.observe(scene))
    this.cleanups.push(() => observer.disconnect())
  }

  // Pointer tilt: eased toward the pointer on each frame while it moves over the stage, back to rest on leave
  bindTilt() {
    if (!this.hasStageTarget || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return

    const stage = this.stageTarget
    const target = { x: 0, y: 0 }
    const current = { x: 0, y: 0 }
    let frame = null

    const step = () => {
      current.x += (target.x - current.x) * 0.12
      current.y += (target.y - current.y) * 0.12
      stage.style.setProperty("--tilt-x", `${current.x.toFixed(2)}deg`)
      stage.style.setProperty("--tilt-y", `${current.y.toFixed(2)}deg`)
      frame = Math.abs(target.x - current.x) + Math.abs(target.y - current.y) > 0.02 ? requestAnimationFrame(step) : null
    }
    const kick = () => { if (!frame) frame = requestAnimationFrame(step) }
    const move = (event) => {
      const rect = stage.getBoundingClientRect()
      target.y = ((event.clientX - rect.left) / rect.width - 0.5) * 16
      target.x = -((event.clientY - rect.top) / rect.height - 0.5) * 12
      kick()
    }
    const leave = () => { target.x = 0; target.y = 0; kick() }

    stage.addEventListener("pointermove", move)
    stage.addEventListener("pointerleave", leave)
    this.cleanups.push(() => {
      stage.removeEventListener("pointermove", move)
      stage.removeEventListener("pointerleave", leave)
      if (frame) cancelAnimationFrame(frame)
    })
  }

  fallbackReveals(gsap) {
    this.revealTargets.forEach(target => {
      gsap.from(target, {
        opacity: 0,
        y: 32,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: { trigger: target, start: "top 88%", once: true }
      })
    })
  }

  // Each stacked card sinks back (scale + shade) while the next card rises over it; the card itself stays opaque
  stackRecede(gsap) {
    const cards = this.stackCardTargets
    cards.slice(0, -1).forEach((card, index) => {
      const inner = card.firstElementChild
      const scrollTrigger = { trigger: cards[index + 1], start: "top bottom", end: "top 25%", scrub: true }
      gsap.to(inner, { scale: 0.92, ease: "none", scrollTrigger })
      gsap.to(inner.querySelector(".landing-stack-shade"), { opacity: 1, ease: "none", scrollTrigger: { ...scrollTrigger } })
    })
  }
}
